import type { EmpiricalStudyConsentChoice } from '@/types';

export interface StoredEmpiricalStudySession {
  sessionId: string;
  gameCode: string;
  participantCode: string;
  playerId: string;
  playerName: string;
  consentChoice: EmpiricalStudyConsentChoice;
  preSurveyResponses: Record<string, string>;
  postSurveyResponses?: Record<string, string>;
  preSurveySubmittedAt?: string;
  gameStartedAt?: string;
  gameCompletedAt?: string;
  gameDurationSeconds?: number;
  finalScore?: number;
  gameMetricsRecordedAt?: string;
  postSurveySubmittedAt?: string;
  lastUpdatedAt: string;
}

const SESSION_STORAGE_KEY = 'homerun_empirical_study_session';
const LOCAL_STORAGE_KEY = 'homerun_empirical_study_session_backup';

const parseStoredSession = (value: string | null): StoredEmpiricalStudySession | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<StoredEmpiricalStudySession>;
    if (
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.gameCode !== 'string' ||
      typeof parsed.participantCode !== 'string' ||
      typeof parsed.playerId !== 'string' ||
      typeof parsed.playerName !== 'string' ||
      (parsed.consentChoice !== 'consent' && parsed.consentChoice !== 'decline') ||
      typeof parsed.lastUpdatedAt !== 'string'
    ) {
      return null;
    }

    return {
      sessionId: parsed.sessionId,
      gameCode: parsed.gameCode,
      participantCode: parsed.participantCode,
      playerId: parsed.playerId,
      playerName: parsed.playerName,
      consentChoice: parsed.consentChoice,
      preSurveyResponses:
        parsed.preSurveyResponses && typeof parsed.preSurveyResponses === 'object'
          ? parsed.preSurveyResponses
          : {},
      postSurveyResponses:
        parsed.postSurveyResponses && typeof parsed.postSurveyResponses === 'object'
          ? parsed.postSurveyResponses
          : undefined,
      preSurveySubmittedAt:
        typeof parsed.preSurveySubmittedAt === 'string'
          ? parsed.preSurveySubmittedAt
          : undefined,
      gameStartedAt: parsed.gameStartedAt,
      gameCompletedAt: parsed.gameCompletedAt,
      gameDurationSeconds: parsed.gameDurationSeconds,
      finalScore: parsed.finalScore,
      gameMetricsRecordedAt: parsed.gameMetricsRecordedAt,
      postSurveySubmittedAt: parsed.postSurveySubmittedAt,
      lastUpdatedAt: parsed.lastUpdatedAt
    };
  } catch (error) {
    console.error('❌ Failed to parse empirical study session data:', error);
    return null;
  }
};

const writeStudySession = (value: StoredEmpiricalStudySession | null): void => {
  try {
    if (!value) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return;
    }

    const serialized = JSON.stringify(value);
    sessionStorage.setItem(SESSION_STORAGE_KEY, serialized);
    localStorage.setItem(LOCAL_STORAGE_KEY, serialized);
  } catch (error) {
    console.error('❌ Failed to store empirical study session data:', error);
  }
};

export const getStoredEmpiricalStudySession = (
  sessionId?: string
): StoredEmpiricalStudySession | null => {
  const fromSession = parseStoredSession(sessionStorage.getItem(SESSION_STORAGE_KEY));
  const fromLocal = parseStoredSession(localStorage.getItem(LOCAL_STORAGE_KEY));
  const stored = fromSession ?? fromLocal;

  if (!stored) {
    return null;
  }

  if (sessionId && stored.sessionId !== sessionId) {
    return null;
  }

  return stored;
};

export const storeEmpiricalStudySession = (
  session: StoredEmpiricalStudySession
): void => {
  writeStudySession(session);
};

export const updateEmpiricalStudySession = (
  updates: Partial<StoredEmpiricalStudySession> & { sessionId?: string }
): StoredEmpiricalStudySession | null => {
  const current = getStoredEmpiricalStudySession(updates.sessionId);
  if (!current) {
    return null;
  }

  const nextValue: StoredEmpiricalStudySession = {
    ...current,
    ...updates,
    lastUpdatedAt: new Date().toISOString()
  };

  writeStudySession(nextValue);
  return nextValue;
};

export const clearEmpiricalStudySession = (sessionId?: string): void => {
  const current = getStoredEmpiricalStudySession();
  if (sessionId && current?.sessionId && current.sessionId !== sessionId) {
    return;
  }

  writeStudySession(null);
};
